import PlanDTO from "../Models/DTOs/Plan";
import {formatMoney} from "./Currency/Currency";
import {
    SubscriptionOffer,
    SubscriptionOfferCadence,
    SubscriptionOfferResolution,
    SubscriptionOfferSource,
} from "../Models/DTOs/SubscriptionOffer";

export type PromotionMode = "acquisition" | "targeted_only" | "annual_upgrade" | "none";
export type BillingOfferAudience = "new" | "restart" | "upgrade";

export function planCadence(plan: PlanDTO): SubscriptionOfferCadence {
    return plan.period === 1 ? "year" : "month";
}

export function promotionRequestForMode(mode: PromotionMode, promotionCode: string | null) {
    switch (mode) {
        case "acquisition":
            return {
                promotionCode,
                audience: "new" as BillingOfferAudience,
                allowedSources: ["targeted", "public"] as SubscriptionOfferSource[],
                allowedCadences: ["month", "year"] as SubscriptionOfferCadence[],
            };
        case "targeted_only":
            return {
                promotionCode,
                audience: "restart" as BillingOfferAudience,
                allowedSources: ["targeted"] as SubscriptionOfferSource[],
                allowedCadences: ["month", "year"] as SubscriptionOfferCadence[],
            };
        case "annual_upgrade":
            return {
                promotionCode: null,
                audience: "upgrade" as BillingOfferAudience,
                allowedSources: ["public"] as SubscriptionOfferSource[],
                allowedCadences: ["year"] as SubscriptionOfferCadence[],
            };
        default:
            return {
                promotionCode: null,
                audience: null,
                allowedSources: [] as SubscriptionOfferSource[],
                allowedCadences: [] as SubscriptionOfferCadence[],
            };
    }
}

export function applyResolvedOffers(
    plans: PlanDTO[],
    resolution: SubscriptionOfferResolution | null,
    allowedSources: SubscriptionOfferSource[],
    allowedCadences: SubscriptionOfferCadence[],
    audience: BillingOfferAudience = "new"
): PlanDTO[] {
    if (!resolution) return plans;

    return plans.map((plan) => {
        const cadence = planCadence(plan);
        const planSource = resolution.plan_sources?.[cadence] ?? resolution.source;
        const sourceAllowed = allowedSources.includes(planSource);
        const candidate = sourceAllowed && allowedCadences.includes(cadence)
            ? resolution.plans?.[cadence]
            : null;
        const offer = audience === "upgrade" && isPrepaidOffer(candidate) ? null : candidate;

        return {
            ...plan,
            offerCatalogResolved: true,
            ...(offer ? {resolvedOffer: offer} : {resolvedOffer: undefined}),
        };
    });
}

export function getResolvedOffer(plan?: PlanDTO | null): SubscriptionOffer | null {
    return plan?.resolvedOffer ?? null;
}

export function getOfferDisplayPrice(plan?: PlanDTO | null): number | null {
    return getResolvedOffer(plan)?.final_amount_minor ?? null;
}

/**
 * Acquisition display fallback. A successful catalog response is authoritative:
 * if it resolves no offer we show the base plan price, not stale legacy copy.
 */
export function getAcquisitionDisplayAmount(plan: PlanDTO): number {
    const offerPrice = getOfferDisplayPrice(plan);
    if (offerPrice != null) return offerPrice;
    if (plan.offerCatalogResolved) return plan.price;
    if (plan.promotionIsActive && plan.promotionPrice != null) return plan.promotionPrice;
    return plan.standardDiscount?.stdPromoPrice ?? plan.price;
}

export function getOfferPercentOff(offer?: SubscriptionOffer | null): number | null {
    if (!offer || isPrepaidOffer(offer) || offer.base_amount_minor <= 0 || offer.amount_off_minor <= 0) return null;
    return Math.round((offer.amount_off_minor / offer.base_amount_minor) * 100);
}

export function getOfferPriceQualifier(offer?: SubscriptionOffer | null): string | null {
    if (!offer) return null;

    if (isPrepaidOffer(offer)) {
        const count = offer.intro_interval_count;
        const interval = offer.intro_interval;
        if (!count || !interval) return "for your introductory period";
        return `for your first ${count} ${interval}${count === 1 ? "" : "s"}`;
    }

    if (offer.duration === "repeating" && offer.duration_months) {
        return `for ${offer.duration_months} ${offer.duration_months === 1 ? "month" : "months"}`;
    }
    if (offer.duration === "once") return null;
    if (offer.duration === "forever") return "ongoing";
    return null;
}

export function isPrepaidOffer(offer?: SubscriptionOffer | null): boolean {
    return offer?.pricing_mode === "prepaid";
}

export function isOfferOnSale(offer?: SubscriptionOffer | null): boolean {
    return !!offer && (isPrepaidOffer(offer) || offer.amount_off_minor > 0);
}

/** Seasonal styling is reserved for discounted offers with a positive priority. */
export function hasSpecialOfferStyling(offer?: SubscriptionOffer | null): boolean {
    return isOfferOnSale(offer) && (offer?.priority ?? 0) > 0;
}

export function getOfferRenewalText(offer?: SubscriptionOffer | null): string | null {
    if (!offer || (!isPrepaidOffer(offer) && offer.duration === "forever")) return null;
    return `Then ${formatMoney(offer.base_amount_minor, offer.currency)}/${offer.cadence}`;
}

export function getAcquisitionPriceSentence(plan: PlanDTO): string {
    const offer = getResolvedOffer(plan);
    const price = formatMoney(getAcquisitionDisplayAmount(plan), offer?.currency ?? plan.currency);
    const qualifier = getOfferPriceQualifier(offer);
    if (!offer || (!isPrepaidOffer(offer) && offer.duration === "forever")) {
        return `${price} per ${planCadence(plan)}`;
    }
    const renewal = getOfferRenewalText(offer);
    const perCycle = !isPrepaidOffer(offer) && offer.duration === "repeating" ? ` per ${offer.cadence}` : "";
    return `${price}${perCycle}${qualifier ? ` ${qualifier}` : ""}${renewal ? `, ${renewal.toLowerCase()}` : ""}`;
}
